import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { AUTH_ENDPOINTS } from '@/features/auth/api/endpoints';
import { useAuthStore } from '@/shared/store/auth.store';
import type { Usuario } from '@/shared/types/api.types';
import type { LoginFormData } from '@/features/auth/schemas/login.schema';

interface LoginRequest {
  email: string;
  password: string;
}

interface SwitchAppRequest {
  appCodigo: string;
}

interface SwitchAppResponse {
  switched: boolean;
}

// [AUD-3.2] Login ahora retorna solo datos del usuario (tokens en HttpOnly cookies)
export function useLoginMutation(): UseMutationResult<Usuario, Error, LoginFormData> {
  const setAuth = useAuthStore(state => state.setAuth);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: LoginFormData) => {
      const request: LoginRequest = {
        email: data.email,
        password: data.password,
      };

      const response = await apiClient<Usuario>(AUTH_ENDPOINTS.LOGIN, {
        method: 'POST',
        body: request,
      });

      return response;
    },
    onSuccess: data => {
      queryClient.removeQueries();
      setAuth(data);
    },
  });
}

export function useSwitchAppMutation(): UseMutationResult<
  SwitchAppResponse,
  Error,
  SwitchAppRequest
> {
  const setActiveApp = useAuthStore(state => state.setActiveApp);
  const availableApps = useAuthStore(state => state.availableApps);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SwitchAppRequest) => {
      const response = await apiClient<SwitchAppResponse>(AUTH_ENDPOINTS.SWITCH_APP, {
        method: 'POST',
        body: data,
      });
      return response;
    },
    onSuccess: (_data, variables) => {
      const app = availableApps.find(a => a.codigo === variables.appCodigo);
      if (app) {
        setActiveApp(app);
      }
      queryClient.invalidateQueries();
    },
  });
}
